import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useClient } from 'urql';

import { OrderTypeValues } from '@sg/shared-be-features-ordering';

import { storePendingPaymentInfo } from '@order/features-ordering';

import {
  CreateSweetpassPaymentIntentDocument,
  type CreateSweetpassPaymentIntentMutation,
  type CreateSweetpassPaymentIntentMutationVariables,
} from './graphql/CreateSweetpassPaymentIntent.generated';

/**
 * Creates a payment intent for the provided sweetpass.
 */
export const useCreateSweetpassPaymentIntent = () => {
  const urqlClient = useClient();
  const { formatMessage } = useIntl();

  return useCallback(
    async (input: CreateSweetpassPaymentIntentMutationVariables['input']) => {
      const response = await urqlClient
        .mutation<CreateSweetpassPaymentIntentMutation>(
          CreateSweetpassPaymentIntentDocument,
          { input },
          { requestPolicy: 'network-only' },
        )
        .toPromise();

      const result = response.data?.createSweetpassPaymentIntent;

      if (result?.__typename === 'SweetpassAlreadyPurchasedError') {
        return { result: 'already-purchased', errorMessage: result.errorMessage } as const;
      }

      if (result?.__typename !== 'PaymentIntent' || !result.id || !result.secret) {
        // Caught by the Stripe integration and shown to the customer.
        throw new Error(formatMessage(messages.error));
      }

      // The order payment state is keyed by the backend order id, so it's stored for polling,
      // including from the home screen if the customer leaves the checkout before it resolves.
      await storePendingPaymentInfo(result.orderId, OrderTypeValues.SweetpassOrder);

      return {
        result: 'created',
        orderId: result.orderId,
        paymentIntentId: result.id,
        clientSecret: result.secret,
      } as const;
    },
    [urqlClient, formatMessage],
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  error: {
    defaultMessage: 'Something went wrong setting up your payment.',
    description: 'Stripe Payment | Create Payment Intent Error',
  },
});
